import React, { useEffect, useState, useCallback } from "react";
import styles from "./Home.module.css";
import SearchBar from "../../Components/Home/SearchBar/SearchBar";
import StoryList from "../../Components/Home/StoryList/StoryList";
import { fetchPublicStories, searchStories } from "../../config/data";
import { debounce } from "../../utils/helpers"; // Add this import

const Home = () => {
  const [stories, setStories] = useState([]);
  const [filteredStories, setFilteredStories] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [isSearchActive, setIsSearchActive] = useState(false);
  const [submittedQuery, setSubmittedQuery] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [searchLoading, setSearchLoading] = useState(false);
  const [hasMore, setHasMore] = useState(true);

  const storiesPerPage = 20;

  // Helper: deduplicate stories by _id
  const dedup = (arr) => {
    const seen = new Set();
    return arr.filter((s) => {
      if (seen.has(s._id)) return false;
      seen.add(s._id);
      return true;
    });
  };

  const loadMoreStories = useCallback(async () => {
    if (loading || !hasMore) return;

    setLoading(true);

    const data = await fetchPublicStories(currentPage, storiesPerPage);
    if (data.length === 0) {
      setHasMore(false);
    } else {
      const sortedStories = data
        .filter((s) => s.thumbnail)
        .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
      setStories((prevStories) => dedup([...prevStories, ...sortedStories]));
      setCurrentPage((prevPage) => prevPage + 1);
    }

    setLoading(false);
  }, [currentPage, loading, hasMore]);

    useEffect(() => {
    const handleScroll = debounce(() => {
      const scrolledToBottom = 
        window.innerHeight + window.pageYOffset >= 
        document.documentElement.offsetHeight - 200;

      if (scrolledToBottom && !loading && hasMore && !searchQuery) {
        loadMoreStories();
      }
    }, 200);

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, [loadMoreStories, loading, hasMore, searchQuery]);

  useEffect(() => {
    loadMoreStories();
  }, [loadMoreStories]);

  const handleSearchKey = (event) => {
    setSearchQuery(event.target.value);
  };

  // Live search: debounce API call as user types
  useEffect(() => {
    const trimmed = searchQuery.trim();
    if (!trimmed) {
      // User cleared the input — exit search mode
      if (isSearchActive) {
        setFilteredStories([]);
        setIsSearchActive(false);
        setSubmittedQuery("");
      }
      return;
    }

    setSearchLoading(true);
    const timer = setTimeout(async () => {
      const results = await searchStories(trimmed);
      setFilteredStories(dedup(results.filter((s) => s.thumbnail)));
      setSubmittedQuery(trimmed);
      setIsSearchActive(true);
      setSearchLoading(false);
    }, 400);

    return () => { clearTimeout(timer); setSearchLoading(false); };
  }, [searchQuery, isSearchActive]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleSearchSubmit = (e) => {
    e.preventDefault();
    // Enter key still works — but live search already handles the logic
  };

  const clearSearch = () => {
    setSearchQuery("");
    setFilteredStories([]);
    setIsSearchActive(false);
    setSubmittedQuery("");
  };

  return (
    <div className={styles.home}>
      {/* Hero Section */}
      <section className={styles.hero}>
        <h1 className={styles.heroTitle}>
          Stories that spark <span className={styles.heroHighlight}>imagination</span>
        </h1>
{/* subtitle removed — was taking up space without adding value */}
      </section>

      <SearchBar
        value={searchQuery}
        handleSearchKey={handleSearchKey}
        clearSearch={clearSearch}
        formSubmit={handleSearchSubmit}
        isSearchActive={isSearchActive}
        submittedQuery={submittedQuery}
        resultCount={filteredStories.length}
      />
      {searchLoading ? (
        <div className={styles.searchLoader}>
          <span className={styles.dot}></span>
          <span className={styles.dot}></span>
          <span className={styles.dot}></span>
        </div>
      ) : isSearchActive && filteredStories.length === 0 ? (
        <p className={styles.emptyState}>
          No stories found for "{submittedQuery}". Try a different word.
        </p>
      ) : (
        <StoryList stories={isSearchActive ? filteredStories : stories} loading={loading && !searchLoading} />
      )}
      {!isSearchActive && !hasMore && stories.length > 0 && (
        <p className={styles.emptyState}>You've seen all the stories!</p>
      )}
    </div>
  );
};

export default Home;
